// WhatsApp-style single-active-tab guard with takeover banner
// Uses BroadcastChannel with localStorage fallback, plus a lease/heartbeat for leader election

const CHANNEL = 'restaurantpos-tab-guard';
const LEASE_KEY = CHANNEL + ':lease';    // stores { ownerId, ts }
const CMD_KEY   = CHANNEL + ':cmd';      // stores commands for fallback channel
const SESSION_KEY = CHANNEL + ':sessionTabId'; // persists across reloads in same tab
const LEASE_TTL = 5000;                  // ms; how long a lease is considered valid without heartbeat
const HEARTBEAT_EVERY = 1500;            // ms
const STARTUP_GRACE_PERIOD = 2500;       // ms; time to allow leadership election before showing banner
const LEADERSHIP_LOSS_GRACE = 800;       // ms; delay before showing banner after losing leadership

// Generate a new runtime tabId for this load
let tabId = (window.generateUUID ? window.generateUUID() : Math.random().toString(36).slice(2)) + ':' + Date.now();
// Read previous tab id from this same tab's session (survives a refresh)
let prevTabId = null; try { prevTabId = sessionStorage.getItem(SESSION_KEY) || null; } catch {}
let isLeader = false;
let hasDeterminedLeadership = false;  // Track if initial leadership election is complete
let inStartupGrace = true;            // During startup, don't show banner while determining leadership
let hbTimer = null;
let bc = null;
let graceTimer = null;                // Timer for leadership loss grace period

function now() { return Date.now(); }

function readLease() {
  try {
    const raw = localStorage.getItem(LEASE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return (data && typeof data === 'object') ? data : null;
  } catch { return null; }
}

function writeLease(ownerId) {
  const data = { ownerId, ts: now() };
  try { localStorage.setItem(LEASE_KEY, JSON.stringify(data)); } catch {}
}

function leaseIsValid(lease) {
  if (!lease) return false;
  return (now() - lease.ts) < LEASE_TTL;
}

function tryAcquireLeadership() {
  const lease = readLease();
  if (!lease || !leaseIsValid(lease)) {
    writeLease(tabId);
    // Re-check: if another tab raced us, its newer write will win
    const check = readLease();
    isLeader = !!check && check.ownerId === tabId;
  } else {
    isLeader = lease.ownerId === tabId;
  }

  // Update heartbeat state
  if (isLeader) {
    startHeartbeat();
    // If we're leader, we're definitely done with startup
    hasDeterminedLeadership = true;
    inStartupGrace = false;
    // Clear any pending grace timer and update UI immediately
    if (graceTimer) { try { clearTimeout(graceTimer); graceTimer = null; } catch {} }
    updateUI();

    // Also ensure button is reset immediately
    if (useHereBtn && bannerEl && bannerEl.style.display !== 'none') {
      useHereBtn.textContent = 'Use here';
      useHereBtn.disabled = false;
    }
  } else {
    stopHeartbeat();
    // If we're not leader and haven't determined yet, we're still in grace period
    if (!hasDeterminedLeadership) {
      inStartupGrace = true;
    } else {
      // If we've already determined leadership (e.g., user clicked "Use here"),
      // update UI immediately
      if (graceTimer) { try { clearTimeout(graceTimer); graceTimer = null; } catch {} }
      updateUI();
    }
  }
}

function renewLease() {
  // Only leader heartbeats; keeps ts fresh
  const lease = readLease();
  if (!lease || lease.ownerId !== tabId) {
    // Lost leadership
    isLeader = false;
    stopHeartbeat();
    hasDeterminedLeadership = true;  // We now know we're not the leader
    inStartupGrace = false;          // No longer in startup grace

    // Clear any pending grace timer and update UI immediately
    if (graceTimer) { try { clearTimeout(graceTimer); graceTimer = null; } catch {} }
    updateUI();  // Show banner immediately for better UX
    return;
  }
  writeLease(tabId);
}

function startHeartbeat() {
  stopHeartbeat();
  hbTimer = setInterval(renewLease, HEARTBEAT_EVERY);
  renewLease();
}
function stopHeartbeat() { if (hbTimer) { clearInterval(hbTimer); hbTimer = null; } }

function sendCmd(cmd) {
  // BroadcastChannel
  if (bc) { try { bc.postMessage(cmd); } catch {} }
  // Fallback via localStorage
  try { localStorage.setItem(CMD_KEY, JSON.stringify({ ...cmd, _ts: now(), _from: tabId })); } catch {}
}

function requestTakeover() { sendCmd({ type: 'takeover-request', to: 'current-leader' }); }

function releaseLeadership() {
  const lease = readLease();
  if (lease && lease.ownerId === tabId) {
    // Let others take it by not renewing; also hint that we’re releasing
    sendCmd({ type: 'leader-releasing', ownerId: tabId });
  }
  isLeader = false; stopHeartbeat(); updateUI();
}

function onCommand(cmd) {
  if (!cmd || typeof cmd !== 'object') return;
  if (cmd.type === 'takeover-request') {
    if (isLeader) {
      // Gracefully release so the requester can acquire
      releaseLeadership();
    }
  } else if (cmd.type === 'leader-releasing') {
    // Another hint to try acquiring soon
    setTimeout(tryAcquireLeadership, 50);
  }
}

function setupChannels() {
  if (typeof window.BroadcastChannel === 'function') {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (ev) => onCommand(ev && ev.data);
  }
  window.addEventListener('storage', (e) => {
    if (!e) return;
    if (e.key === CMD_KEY && e.newValue) {
      try { onCommand(JSON.parse(e.newValue)); } catch {}
    } else if (e.key === LEASE_KEY) {
      // Someone else renewed or changed lease; update status
      const lease = readLease();
      const oldIsLeader = isLeader;
      isLeader = !!lease && lease.ownerId === tabId && leaseIsValid(lease);

      if (oldIsLeader !== isLeader) {
        if (isLeader) {
          // We became leader
          hasDeterminedLeadership = true;
          inStartupGrace = false;
          startHeartbeat();
          if (graceTimer) { try { clearTimeout(graceTimer); graceTimer = null; } catch {} }
          updateUI();

          // Also ensure button is reset immediately
          if (useHereBtn && bannerEl && bannerEl.style.display !== 'none') {
            useHereBtn.textContent = 'Use here';
            useHereBtn.disabled = false;
          }
        } else {
          // We lost leadership
          stopHeartbeat();
          hasDeterminedLeadership = true;
          inStartupGrace = false;

          // Clear timer and show banner immediately for better UX
          if (graceTimer) { try { clearTimeout(graceTimer); graceTimer = null; } catch {} }
          updateUI();
        }
      }
    }
  });
}

// ---- UI banner and interaction blocker (WhatsApp-style) ----
let bannerEl = null;
let useHereBtn = null;
let overlayEl = null;

function ensureBanner() {
  if (bannerEl) return bannerEl;
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;background:#fff3cd;color:#664d03;border-bottom:1px solid #ffe69c;padding:10px 12px;font-size:14px;display:none;gap:12px;align-items:center;justify-content:space-between;box-shadow:0 2px 6px rgba(0,0,0,0.08)';
  el.innerHTML = `
    <div>Retail pos is open in another tab. Click "Use here" to make this the active tab.</div>
    <div style="display:flex;gap:8px;align-items:center;">
      <button id="use-here-btn" style="background:#0d6efd;color:#fff;border:none;border-radius:4px;padding:6px 10px;cursor:pointer;opacity:1;">Use here</button>
    </div>
  `;
  document.body.appendChild(el);
  useHereBtn = el.querySelector('#use-here-btn');
  if (useHereBtn) {
    useHereBtn.addEventListener('click', () => {
      // Give immediate visual feedback
      const originalText = useHereBtn.textContent;
      useHereBtn.textContent = 'Taking over...';
      useHereBtn.disabled = true;

      requestTakeover();
      // Try acquiring shortly after asking leader to release
      setTimeout(() => {
        tryAcquireLeadership();

        // If we successfully became leader, reset button immediately
        if (isLeader) {
          // Button will be reset by hideBanner() via updateUI()
        } else {
          // Failed to take over, re-enable after a short delay
          setTimeout(() => {
            if (!isLeader && bannerEl && bannerEl.style.display !== 'none') {
              useHereBtn.textContent = originalText;
              useHereBtn.disabled = false;
            }
          }, 500);
        }
      }, 200); // Single attempt with slightly longer delay
    });
  }
  bannerEl = el;
  return el;
}

function ensureOverlay() {
  if (overlayEl) return overlayEl;
  const overlay = document.createElement('div');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.tabIndex = 0;
  overlay.style.cssText = [
    'position:fixed',
    'top:0','left:0','right:0','bottom:0',
    'z-index:99998',
    // Stronger dim plus blur so the app looks clearly disabled
    'background:rgba(0,0,0,0.35)',
    'backdrop-filter:blur(3px)',
    '-webkit-backdrop-filter:blur(3px)',
    'display:none',
    'pointer-events:auto', // capture clicks/taps so the app underneath cannot be interacted with
    'cursor:not-allowed'
  ].join(';');
  // Optional helper text centered
  const msg = document.createElement('div');
  msg.textContent = 'This tab is inactive. Click "Use here" at the top to activate it.';
  msg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(255,255,255,0.9);color:#333;border:1px solid #eee;border-radius:6px;padding:8px 12px;font-size:13px;box-shadow:0 1px 3px rgba(0,0,0,0.06)';
  overlay.appendChild(msg);
  // Swallow clicks/touches
  overlay.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, true);
  overlay.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation(); }, true);
  overlay.addEventListener('touchstart', (e) => { e.preventDefault(); e.stopPropagation(); }, true);
  document.body.appendChild(overlay);
  overlayEl = overlay;
  return overlay;
}

function keydownBlock(e) {
  if (!overlayEl || overlayEl.style.display === 'none') return;
  // Allow refresh and devtools
  if (e.key === 'F5' || e.key === 'F12' || ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R'))) return;
  e.preventDefault();
  e.stopImmediatePropagation();
}

function showOverlay() {
  const ov = ensureOverlay();
  ov.style.display = 'block';
  // Blur any focused element to avoid typing into inputs
  try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch {}
  window.addEventListener('keydown', keydownBlock, true);
}

function hideOverlay() {
  if (overlayEl) overlayEl.style.display = 'none';
  window.removeEventListener('keydown', keydownBlock, true);
}

function showBanner() {
  const el = ensureBanner();
  el.style.display = 'flex';
  // Button is now always enabled for immediate takeover
  if (useHereBtn) {
    useHereBtn.disabled = false;
    useHereBtn.style.cursor = 'pointer';
    useHereBtn.style.opacity = '1';
  }
}

function hideBanner() {
  if (bannerEl) bannerEl.style.display = 'none';
  // Reset button state when hiding banner
  if (useHereBtn) {
    useHereBtn.textContent = 'Use here';
    useHereBtn.disabled = false;
  }
}

function updateUI() {
  // Don't show banner/overlay during grace periods
  const shouldShowInactiveUI = !isLeader && !inStartupGrace && hasDeterminedLeadership;

  if (isLeader) { hideBanner(); hideOverlay(); }
  else if (shouldShowInactiveUI) { showBanner(); showOverlay(); }

  // Notify app if EventBus exists (only after we've determined leadership)
  if (hasDeterminedLeadership && window.EventBus && window.EventBus.$emit) {
    try { window.EventBus.$emit('tab-leadership-changed', { isLeader, tabId }); } catch {}
  }
}

export function initTabGuardLeader() {
  setupChannels();

  // Fast-path for same-tab refresh: if a valid lease exists owned by our previous tab id,
  // immediately take over without showing the inactive overlay.
  try {
    const lease = readLease();
    if (lease && leaseIsValid(lease) && prevTabId && lease.ownerId === prevTabId) {
      writeLease(tabId);
      isLeader = true;
      startHeartbeat();
      hasDeterminedLeadership = true;
      inStartupGrace = false;
      updateUI();
    }
  } catch {}

  // Try to become leader on load if there is no valid leader (or if fast-path didn't already make us leader)
  if (!isLeader) {
    tryAcquireLeadership();
  }

  // Persist our new runtime tab id for future refresh detection
  try { sessionStorage.setItem(SESSION_KEY, tabId); } catch {}

  // Set up startup grace period: after this time, we'll show the banner if we're not leader
  setTimeout(() => {
    inStartupGrace = false;
    hasDeterminedLeadership = true;
    // Only update UI if we've determined we're not the leader
    if (!isLeader) {
      updateUI();
    }
  }, STARTUP_GRACE_PERIOD);

  // Re-evaluate on visibility changes (common user action that implies intent)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Don't reset grace period if we've already determined leadership
      // Just try to acquire again (someone else might have released)
      tryAcquireLeadership();
    }
  });

  window.addEventListener('beforeunload', () => {
    if (isLeader) {
      // Hint to others that we're leaving
      sendCmd({ type: 'leader-releasing', ownerId: tabId });
    }
    // Clean up timers
    if (graceTimer) { try { clearTimeout(graceTimer); } catch {} }
  });

  const api = {
    isLeader: () => isLeader,
    forceTakeover: () => { requestTakeover(); setTimeout(tryAcquireLeadership, 120); },
    stop: () => {
      stopHeartbeat();
      if (graceTimer) { try { clearTimeout(graceTimer); } catch {} }
      if (bc) try { bc.close(); } catch {}
    }
  };

  // Optionally expose on window for debugging/integration
  try { window.tabGuardLeader = api; } catch {}

  return api;
}
